import { planTipsFor, type TourPlan } from '@/features/onboarding/lib/welcome-plan'
import {
  PAGE_TOUR_TIPS,
  type TradingExperience,
  type WelcomeTip,
  welcomeTipsFor,
} from '@/features/onboarding/lib/welcome-tips'

/**
 * The real element each tour step spotlights. The tour runs over the live workspace, so these
 * are the actual controls the step asks the user to try, not pictures of them.
 */
export const TOUR_TARGETS: Record<WelcomeTip, string | null> = {
  palette: '[data-tour="palette"]',
  shortcuts: '[data-tour="hide-balances"]',
  linkGroups: '[data-tour="link-dot"]',
  templates: '[data-workspace-trigger]',
  riskSlides: '[data-component-key="risk-slides"]',
  orderForm: '[data-component-key="chat-order-form"]',
  connect: null,
  watchlist: '[data-component-key="watchlist"]',
  chart: '[data-component-key="price-chart"]',
  balance: '[data-component-key="account-cards"]',
  positions: '[data-component-key="positions"]',
  // The no-account call to action in Positions / Account balance.
  addAccount: '[data-tour="add-account"]',
  // The plan's table is a card of its own; its pages are the side panel's rows.
  plan: null,
  riskSlidesNav: '[data-nav-item="risk-slides"]',
  hedgeNav: '[data-nav-item="delta-hedge"]',
  arbNav: '[data-nav-item="arbitrage"]',
  team: null,
  tutorials: '[data-tour="tutorials"]',
}

export interface TourContext {
  /** A Risk Slides widget is on the page to point at. */
  hasRiskSlides: boolean
  /** A widget header with a link dot is on the page. */
  hasLinkDot: boolean
  /** The user already has an active exchange account. */
  hasAccount: boolean
}

/**
 * The steps to run for an answer: the experience's tips, minus the ones there's nothing real to
 * show for on this page (no Risk Slides widget, no widget to link) or nothing left to do
 * (already connected).
 */
export function tourStepsFor(experience: TradingExperience, ctx: TourContext): WelcomeTip[] {
  return welcomeTipsFor(experience).filter((tip) => {
    if (tip === 'riskSlides') return ctx.hasRiskSlides
    if (tip === 'linkGroups') return ctx.hasLinkDot
    if (tip === 'connect') return !ctx.hasAccount
    return true
  })
}

/** The walkthrough of a page the tour set up, minus adding an account when there already is one. */
export function pageTourSteps(ctx: Pick<TourContext, 'hasAccount'>): WelcomeTip[] {
  return PAGE_TOUR_TIPS.filter((tip) => tip !== 'addAccount' || !ctx.hasAccount)
}

export interface PlanTourContext {
  /** A plan step the plan can't use (false) is dropped: the tour never points at a lock. */
  allows: Partial<Record<WelcomeTip, boolean>>
  /** Whether a step's target is on screen (the side panel is desktop-only). */
  onScreen: (selector: string) => boolean
}

/**
 * The plan's steps after the basics, minus what the plan doesn't include and what isn't on
 * screen to point at. A step with no target (the plan table, the team card) always runs.
 */
export function planTourSteps(plan: TourPlan | null, ctx: PlanTourContext): WelcomeTip[] {
  return planTipsFor(plan).filter((tip) => {
    if (ctx.allows[tip] === false) return false
    const target = TOUR_TARGETS[tip]
    return target === null || ctx.onScreen(target)
  })
}

export interface Rect {
  left: number
  top: number
  width: number
  height: number
}

export type TourSide = 'top' | 'right' | 'bottom' | 'left'

/**
 * Which side of the target the step card goes on: the one with the most room to spare for the
 * card, so the real control stays visible and usable next to it. Ties keep the card to the
 * right. The popover falls back on its own if the card doesn't fit there after all.
 */
export function preferredTourSide(
  target: Rect,
  viewport: { width: number; height: number },
  card: { width: number; height: number }
): TourSide {
  const spare: Record<TourSide, number> = {
    right: viewport.width - (target.left + target.width) - card.width,
    left: target.left - card.width,
    bottom: viewport.height - (target.top + target.height) - card.height,
    top: target.top - card.height,
  }
  const sides = Object.keys(spare) as TourSide[]
  return sides.reduce((best, side) => (spare[side] > spare[best] ? side : best), sides[0])
}
